// ═══════════════════════════════════════════════════════════════════════════════
//  PANTALLA 5.5 — Captura de Contacto  [NUEVO — v2]
//  docs/onboarding-master-v2.md, Parte I §5.bis
//
//  Es el único punto del funnel donde un abandono se vuelve remarketable.
//  Reglas de producto que este componente respeta al pie de la letra:
//    · Se pide como "guardar mi resultado", nunca como "necesitamos tus datos".
//    · Email requerido, teléfono opcional.
//    · Salida secundaria NO bloqueante: un link de texto pequeño, no un botón.
//      Al usarlo se registra contact_soft_capture: "skipped" — el equipo debe
//      saber que ese perfil existe y en qué paso decidió no dejar sus datos.
//
//  El sync al backend ocurre AQUÍ (Punto de Decisión A — ver onboardingStore.js).
//  Es best-effort: si el backend falla, el usuario continúa sin enterarse.
//
//  ── EL TELÉFONO, DESDE EL 24-09-2026 ────────────────────────────────────────
//  Era un campo libre de hasta 30 caracteres con el placeholder «Opcional», y
//  con eso llegaban números a medias, con lada de otro país o con letras. Rob:
//  «si es poner un número de WhatsApp debemos tener lada iniciando en MX +52 y
//  el número no mayor a 10 dígitos… al presionar poner una alerta de a
//  continuación vamos a enviar un WhatsApp con tus resultados y un botón de
//  confirmación o aceptar».
//
//  Tres decisiones detrás de cómo quedó:
//
//  1. LA LADA NO SE ESCRIBE, SE ENSEÑA. El «+52» va fijo a la izquierda del
//     campo, y dentro solo caben diez cifras. Así nadie puede escribir la lada
//     dos veces ni dejarla fuera, que era la mitad de los números rotos.
//
//  2. LA CONFIRMACIÓN DICE LA VERDAD. NSL no tiene pasarela de WhatsApp: los
//     mensajes los escribe gerencia desde su propio número. Así que la pantalla
//     promete exactamente eso —«te escribimos nosotros»— y no un envío
//     automático que no existe. Prometer un mensaje que no llega en treinta
//     segundos quema el número y la confianza a la vez.
//
//  3. ACEPTAR ES EL CONSENTIMIENTO. Ese botón es lo que convierte un número
//     guardado en un número que se puede usar, y queda registrado en el lead
//     (`captured_whatsapp_ok`). Sin ese paso el número se guarda igual, pero
//     como dato de contacto pasivo: nadie le escribe.
// ═══════════════════════════════════════════════════════════════════════════════

import { useState } from 'react';
import { CONTACT_CAPTURE } from './onboardingContent';
import {
  TELEFONO_MX,
  soloDigitos,
  formateaTelefonoMX,
  telefonoCompleto,
  telefonoValido,
} from '../../limites';

// Validación de email deliberadamente permisiva en el cliente: el backend ya
// valida en serio con EmailStr de Pydantic. Aquí solo evitamos el typo obvio.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/**
 * El enlace al aviso de privacidad.
 *
 * Esta es la única pantalla del diagnóstico donde se piden datos personales, y
 * quien la ve todavía no tiene cuenta: no ha aceptado nada en ningún sitio. El
 * aviso tiene que estar a un toque, y abrirse en otra pestaña — mandar a nadie
 * fuera a mitad del embudo es perderlo.
 */
function AvisoDePrivacidad() {
  return (
    <a
      className="ob-enlace-legal"
      href="/privacidad"
      target="_blank"
      rel="noreferrer"
      data-prueba="aviso-privacidad"
    >
      Cómo cuidamos tus datos
    </a>
  );
}

export default function Screen5_5_CapturaContacto({ onCapture, onSkip }) {
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');          // solo dígitos, sin lada
  const [error, setError] = useState(null);
  const [errorTel, setErrorTel] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmando, setConfirmando] = useState(false);

  const hayTelefono = phone.length > 0;
  const telefonoListo = telefonoValido(phone);

  /** Guarda de verdad. `conWhatsApp` es el consentimiento explícito. */
  const guarda = async (conWhatsApp) => {
    if (saving) return;
    setSaving(true);
    await onCapture({
      email: email.trim(),
      phone: conWhatsApp ? telefonoCompleto(phone) : null,
      whatsappOk: conWhatsApp,
    });
    setSaving(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (saving) return;

    if (!EMAIL_RE.test(email.trim())) {
      setError('Revisa tu email — parece que le falta algo.');
      return;
    }
    setError(null);

    // Un teléfono a medias se avisa aquí, no se manda roto al servidor.
    if (hayTelefono && !telefonoListo) {
      setErrorTel(`Faltan cifras: un número de México lleva ${TELEFONO_MX.digitos}.`);
      return;
    }
    setErrorTel(null);

    // Sin teléfono no hay nada que confirmar: se guarda y sigue.
    if (!hayTelefono) {
      guarda(false);
      return;
    }
    setConfirmando(true);
  };

  /* ── El paso de confirmación ───────────────────────────────────────────── */

  if (confirmando) {
    return (
      <div className="ob-screen">
        <p className="ob-eyebrow">Un último detalle</p>
        <h1 className="ob-headline">
          <span className="ob-headline-linea">Te vamos a escribir por WhatsApp a este número.</span>
          <span className="ob-bloque">¿Lo confirmas?</span>
        </h1>

        <div className="ob-numero-confirma" data-prueba="numero-confirmado">
          <span className="ob-numero-lada">{TELEFONO_MX.lada}</span>
          <span className="ob-numero-cifras">{formateaTelefonoMX(phone)}</span>
        </div>

        <ul className="ob-promises">
          <li className="ob-promise">
            <span className="ob-promise-icon" aria-hidden="true">💬</span>
            <span>Te mandamos tu resultado y tu plan de entrenamiento.</span>
          </li>
          <li className="ob-promise">
            <span className="ob-promise-icon" aria-hidden="true">🙋</span>
            <span>Escribimos nosotros, desde el WhatsApp de NSL. No es un robot.</span>
          </li>
          <li className="ob-promise">
            <span className="ob-promise-icon" aria-hidden="true">🚪</span>
            <span>Si no quieres más mensajes, respondes «BAJA» y se acabó.</span>
          </li>
        </ul>

        <p className="ob-honesty">
          Tu número se guarda solo para esto. No se comparte con nadie ni se usa
          para llamarte a vender. <AvisoDePrivacidad />
        </p>

        <div className="ob-actions">
          <button
            type="button"
            className="ob-btn"
            data-prueba="acepta-whatsapp"
            disabled={saving}
            onClick={() => guarda(true)}
          >
            {saving ? 'Guardando...' : 'Sí, escríbanme →'}
          </button>
          <button
            type="button"
            className="ob-textlink"
            disabled={saving}
            onClick={() => guarda(false)}
          >
            Mejor solo por correo
          </button>
          <button
            type="button"
            className="ob-textlink"
            disabled={saving}
            onClick={() => setConfirmando(false)}
          >
            Corregir el número
          </button>
        </div>
      </div>
    );
  }

  /* ── El formulario ─────────────────────────────────────────────────────── */

  return (
    <div className="ob-screen">
      <p className="ob-eyebrow">Guarda tu resultado</p>
      <h1 className="ob-headline">{CONTACT_CAPTURE.headline}</h1>

      <form className="ob-actions" onSubmit={handleSubmit} noValidate>
        <div className="ob-field">
          <label className="ob-label" htmlFor="ob-email">
            <span aria-hidden="true">{CONTACT_CAPTURE.emailIcon}</span>
            {CONTACT_CAPTURE.emailLabel}
          </label>
          <input
            maxLength={254}
            id="ob-email"
            type="email"
            inputMode="email"
            autoComplete="email"
            className={`ob-input${error ? ' ob-input-error' : ''}`}
            placeholder="tu@email.com"
            value={email}
            onChange={(e) => { setEmail(e.target.value); setError(null); }}
            required
          />
          {error && <p className="ob-error-text">{error}</p>}
        </div>

        <div className="ob-field">
          <label className="ob-label" htmlFor="ob-phone">
            <span aria-hidden="true">{CONTACT_CAPTURE.phoneIcon}</span>
            {CONTACT_CAPTURE.phoneLabel}
          </label>

          {/* La lada va fuera del campo: no se escribe, no se borra, no se
              duplica. Dentro solo caben las diez cifras del número. */}
          <div className={`ob-tel${errorTel ? ' ob-tel-error' : ''}`}>
            <span className="ob-tel-lada" aria-hidden="true">{TELEFONO_MX.lada}</span>
            <input
              id="ob-phone"
              type="tel"
              inputMode="numeric"
              autoComplete="tel-national"
              className="ob-tel-campo"
              placeholder="55 1234 5678"
              // El tope de verdad son las diez cifras, y vive en el estado
              // (`soloDigitos`), que es por donde pasa todo: teclear, pegar y
              // cualquier camino que se escriba más adelante.
              //
              // `maxLength` aquí NO puede valer 12 —lo que mide el número ya
              // formateado—, porque recorta el texto crudo ANTES de que llegue
              // al estado: pegar «(55) 1234-5678 ext 99» se quedaba en nueve
              // cifras y el alumno veía desaparecer un dígito sin saber por
              // qué. Se deja holgado, solo como freno a un pegado enorme.
              maxLength={24}
              value={formateaTelefonoMX(phone)}
              onChange={(e) => { setPhone(soloDigitos(e.target.value)); setErrorTel(null); }}
              aria-describedby="ob-phone-ayuda"
            />
            <span className="ob-tel-cuenta" aria-hidden="true">
              {phone.length}/{TELEFONO_MX.digitos}
            </span>
          </div>

          {errorTel && <p className="ob-error-text">{errorTel}</p>}
          <p className="ob-microcopy" id="ob-phone-ayuda">{CONTACT_CAPTURE.phoneMicrocopy}</p>
        </div>

        <p className="ob-honesty">
          {CONTACT_CAPTURE.trustMicrocopy} <AvisoDePrivacidad />
        </p>

        <button type="submit" className="ob-btn" disabled={saving}>
          {saving ? 'Guardando...' : CONTACT_CAPTURE.primaryButton}
        </button>

        {/* Salida secundaria: link de texto, NUNCA un botón (Parte I §5.bis) */}
        <button type="button" className="ob-textlink" onClick={onSkip} disabled={saving}>
          {CONTACT_CAPTURE.skipLink}
        </button>
      </form>
    </div>
  );
}
